export const BEHAVIORAL_LANGUAGE = {

    narrative_transitions: {

    reinforcement: [
      "At the same time",
      "Additionally",
      "In many cases",
      "This may be especially noticeable where"
    ],

    caution: [
      "However",
      "At the same time",
      "Some aspects of the program",
      "In certain academic situations"
    ],

    adaptation: [
      "Over time",
      "With experience",
      "As you adjust to the program",
      "With deliberate development"
    ]
  },

  structure_alignment: {
    analytical_thinker:
      "From your responses, it appears that you like to think carefully before making decisions. This programme encourages that approach because you'll often be expected to analyse information before taking action.",

    strategic_planner:
      "It appears that you prefer working in an organised and structured way. This programme rewards careful planning and following clear processes throughout your studies.",

    practical_operator: {

      high:
        "You are likely to feel comfortable in this programme because many of its courses have clear expectations and a well-organised way of learning.",

      moderate:
        "Many parts of this programme are likely to suit the way you like to learn, although some courses may ask you to become comfortable with different approaches over time.",

      low:
        "This programme may sometimes expect you to learn in ways that feel less familiar at first, but it also gives you opportunities to build new skills as you progress."
    },

    independent_problem_solver:
      "It appears that you enjoy working through problems on your own before seeking help. This programme provides many opportunities to think independently while developing practical solutions.",

    creative_explorer:
      "Although this programme follows a structured approach, it also gives you opportunities to explore different ideas and ways of solving problems, which matches your learning style.",

    versatile_collaborator:
      "It appears that you work well with others while still appreciating clear direction. This programme combines teamwork with organised learning in a balanced way."
  },

  technical_alignment: {
    analytical_thinker:
      "The program's analytical and systems-oriented environment aligns well with your preference for logical reasoning and structured problem-solving.",

    independent_problem_solver:
      "The program provides opportunities for independent technical exploration that complement your problem-solving approach and ability to work autonomously.",

    creative_explorer:
      "Innovation-focused technical environments provide space for experimentation while allowing you to apply creative thinking to practical challenges.",

    practical_operator:
      "The structured technical nature of the program complements your preference for practical execution and clearly defined processes.",

    strategic_planner:
      "The program's emphasis on systematic analysis and long-term problem-solving aligns well with your planning-oriented approach.",

    versatile_collaborator:
      "The technical environment combines analytical work with collaborative execution in ways that suit your balanced working style."
  },

  ambiguity_tension: {
    analytical_thinker:
      "highly ambiguous or loosely structured environments may occasionally feel mentally demanding",

    practical_operator:
      "rapidly changing expectations or undefined workflows may require adjustment",

    strategic_planner:
      "situations lacking clear direction or structure may occasionally create friction"
  },

  collaboration_tension: {
    independent_problem_solver:
      "highly collaborative and socially intensive environments may occasionally feel draining",

    analytical_thinker:
      "constant collaborative interaction may occasionally interrupt your preferred deep-focus working style"
  },

  creativity_tension: {
    analytical_thinker:
      "highly open-ended and creatively unstructured environments may occasionally feel uncomfortable if you naturally prefer logical systems and clearly defined expectations",

    strategic_planner:
      "creative environments with fluid direction and minimal structure may sometimes conflict with your preference for planning and predictability",

    practical_operator:
      "highly abstract or experimentation-driven environments may occasionally feel disconnected from your preference for practical execution"
  },

  emotional_tension: {
    analytical_thinker:
      "emotionally intensive and people-centered environments may occasionally feel draining if you naturally prefer analytical distance and objective problem-solving",

    independent_problem_solver:
      "constant emotional engagement and interpersonal demands may sometimes conflict with your preference for independence and focused problem-solving"
  },

  technical_tension: {
    versatile_collaborator:
      "the deeply technical and independently focused nature of the field may occasionally feel isolating",

    creative_explorer:
      "areas requiring sustained technical precision and repetitive analytical focus may sometimes feel restrictive"
  },

  adaptability_strength: {
    creative_explorer:
      "your curiosity and adaptability may become strengths in innovation-oriented environments",

    versatile_collaborator:
      "your interpersonal adaptability may help you navigate collaborative academic environments"
  },


// SEMANTIC ENVIRONMENT LANGUAGE

environment_semantics: {

  alignments: {

    structured_reasoning:
      "Your preference for structured reasoning aligns well with environments that emphasize clear processes, logical thinking, and organized problem-solving.",

    technical_environment:
      "Your technical orientation appears well suited to environments that emphasize systems thinking, practical implementation, and analytical problem-solving.",

    leadership_alignment:
      "Your behavioral profile suggests comfort in environments that encourage responsibility, initiative, and leadership development.",

    creative_environment:
      "Your openness to creativity aligns well with environments that encourage innovation, exploration, and original thinking.",

    adaptive_environment:
      "Your adaptability may help you respond effectively to changing expectations and unfamiliar academic challenges.",

    independent_execution:
      "Your preference for independent work aligns well with environments that reward self-direction and personal responsibility.",

    structured_environment:
      "You are likely to feel comfortable in learning environments that provide structure, clear expectations, and consistent routines."

  },

  tensions: {

    high_ambiguity:
      "The programme may sometimes require working with uncertainty, incomplete information, or open-ended problems.",

    emotional_intensity:
      "Some situations within the programme may require greater emotional resilience and sustained interpersonal engagement.",

    low_structure_environment:
      "The learning environment may occasionally feel less structured than you naturally prefer, requiring greater personal organisation.",

    high_collaboration:
      "Success may require frequent collaboration and teamwork, which could demand additional behavioural adjustment if you naturally prefer working independently."

  },

  structured_alignment: [
    "clear instructions",
    "organized learning",
    "knowing what is expected",
    "step-by-step problem solving",
    "learning with a clear plan"
  ],

  structured_tension: [
    "unclear instructions",
    "changing expectations",
    "open-ended assignments",
    "learning without a fixed structure"
  ],

  creative_alignment: [
    "trying new ideas",
    "creative projects",
    "thinking in different ways",
    "exploring different solutions"
  ],

  creative_tension: [
    "repetitive tasks",
    "strict rules",
    "doing things one fixed way",
    "highly structured routines"
  ],

  communication_alignment: [
    "working with other students",
    "sharing ideas",
    "group discussions",
    "presentations",
    "meeting new people"
  ],

  communication_tension: [
    "frequent group work",
    "regular presentations",
    "constant interaction with others",
    "working closely with large teams"
  ],

  analytical_alignment: [
    "solving problems step by step",
    "working with facts and evidence",
    "careful thinking",
    "logical decision-making"
  ],

  analytical_tension: [
    "making decisions without clear answers",
    "open-ended questions",
    "interpreting different viewpoints",
    "situations with no single correct solution"
  ],
},

behavior_translation: {

  structurePreference: {
    high:
      "You enjoy working with clear plans and well-organised tasks.",
    moderate:
      "You appreciate having some structure while still being flexible when needed.",
    low:
      "You're comfortable figuring things out as situations change."
  },

  technicalOrientation: {
    high:
      "You enjoy understanding how things work and solving technical problems.",
    moderate:
      "You don't mind technical work when it has a practical purpose.",
    low:
      "You generally prefer ideas and people over highly technical work."
  },

  creativityOrientation: {
    high:
      "You enjoy exploring new ideas and thinking of different ways to solve problems.",
    moderate:
      "You like opportunities to be creative while still working toward clear goals.",
    low:
      "You usually prefer practical and proven approaches over experimenting."
  },

  collaborationPreference: {
    high:
      "You enjoy working closely with other people and sharing ideas.",
    moderate:
      "You're comfortable working both independently and as part of a team.",
    low:
      "You enjoy having time to work independently and think things through on your own."
  },

  ambiguityTolerance: {
    high:
      "You're comfortable dealing with situations where there isn't always one clear answer.",
    moderate:
      "You can adapt when things are uncertain, especially after understanding the situation.",
    low:
      "You prefer having clear expectations before getting started."
  },

  adaptabilityPreference: {
    high:
      "You're usually comfortable adjusting to new situations and learning as you go.",
    moderate:
      "You can adapt to change while still appreciating some consistency.",
    low:
      "You prefer familiar routines and making changes gradually."
  }

},
};